import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bell, CheckCircle, Sparkles, Clock, Mail, Loader2, AlertCircle } from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';
import { subscribeNewsletter } from '../lib/api';

export default function ComingSoonPage({ title = 'Coming Soon', description = 'This page is under construction.' }) {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError(null);

    try {
      await subscribeNewsletter(email);
      setIsSubscribed(true);
      setEmail('');
    } catch (err) {
      setError(err.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-20 flex items-center justify-center">
      <AnimatedBackground variant="gaming" />
      <ParticleBackground density={20} />

      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <GlowingCard glowColor="blue">
          <div className="p-12">
            {/* Icon */}
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 flex items-center justify-center mx-auto mb-8">
              <Clock className="w-10 h-10 text-blue-400" />
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">Coming Soon</span>
            </div>

            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {title}
            </h1>

            <p className="text-lg text-slate-400 mb-8 max-w-md mx-auto">
              {description}
            </p>

            {/* Email Signup */}
            {!isSubscribed ? (
              <form onSubmit={handleSubmit} className="max-w-sm mx-auto mb-8">
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm mb-4">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <p>{error}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white transition-all shrink-0 disabled:opacity-50"
                  >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Bell className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Get notified when this page is ready
                </p>
              </form>
            ) : (
              <div className="flex items-center justify-center gap-2 mb-8 text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span>You'll be notified when this page is ready!</span>
              </div>
            )}

            {/* Back Link */}
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </GlowingCard>

        {/* Quick Links */}
        <div className="mt-12">
          <p className="text-sm text-slate-500 mb-4">In the meantime, check out:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: 'Features', href: '/features' },
              { name: 'About Us', href: '/about' },
            ].map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:border-blue-500/50 transition-all text-sm"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
